/**
 * Populate claims table with appropriate description
 * @param {Object} claims ID token claims
 * @returns claimsObject
 */
export const createClaimsTable = (claims: any) => {
  const claimsObj: TClaimsObject = {};
  let index = 0;

  Object.keys(claims).forEach((key) => {
    if (typeof claims[key] !== 'string' && typeof claims[key] !== 'number' && !Array.isArray(claims[key])) return;
    switch (key) {
      case 'aud':
        populateClaim(
          key,
          claims[key],
          "Identifies the intended recipient of the token. In ID tokens, the audience is your app's Application ID, assigned to your app in the Azure portal.",
          index,
          claimsObj,
        );
        index = index + 1;
        break;
      case 'iss':
        populateClaim(
          key,
          claims[key],
          'Identifies the issuer, or authorization server that constructs and returns the token. It also identifies the Azure AD tenant for which the user was authenticated. If the token was issued by the v2.0 endpoint, the URI will end in /v2.0. The GUID that indicates that the user is a consumer user from a Microsoft account is 9188040d-6c67-4c5b-b112-36a304b66dad.',
          index,
          claimsObj,
        );
        index = index + 1;
        break;
      case 'iat':
        populateClaim(
          key,
          changeDateFormat(claims[key]),
          '"Issued At" indicates the timestamp (UNIX timestamp) when the authentication for this user occurred.',
          index,
          claimsObj,
        );
        index = index + 1;
        break;
      case 'nbf':
        populateClaim(
          key,
          changeDateFormat(claims[key]),
          'The nbf (not before) claim dictates the time (as UNIX timestamp) before which the JWT must not be accepted for processing.',
          index,
          claimsObj,
        );
        index = index + 1;
        break;
      case 'exp':
        populateClaim(
          key,
          changeDateFormat(claims[key]),
          "The exp (expiration time) claim dictates the expiration time (as UNIX timestamp) on or after which the JWT must not be accepted for processing. It's important to note that in certain circumstances, a resource may reject the token before this time. For example, if a change in authentication is required or a token revocation has been detected.",
          index,
          claimsObj,
        );
        index = index + 1;
        break;
      case 'name':
        populateClaim(
          key,
          claims[key],
          "The name claim provides a human-readable value that identifies the subject of the token. The value isn't guaranteed to be unique, it can be changed, and it's designed to be used only for display purposes. The 'profile' scope is required to receive this claim.",
          index,
          claimsObj,
        );
        index = index + 1;
        break;
      case 'preferred_username':
        populateClaim(
          key,
          claims[key],
          'The primary username that represents the user. It could be an email address, phone number, or a generic username without a specified format. Its value is mutable and might change over time. Since it is mutable, this value must not be used to make authorization decisions. It can be used for username hints, however, and in human-readable UI as a username. The profile scope is required in order to receive this claim.',
          index,
          claimsObj,
        );
        index = index + 1;
        break;
      case 'nonce':
        populateClaim(
          key,
          claims[key],
          'The nonce matches the parameter included in the original /authorize request to the IDP. If it does not match, your application should reject the token.',
          index,
          claimsObj,
        );
        index = index + 1;
        break;
      case 'oid':
        populateClaim(
          key,
          claims[key],
          'The oid (user’s object id) is the only claim that should be used to uniquely identify a user in an Azure AD tenant.',
          index,
          claimsObj,
        );
        index = index + 1;
        break;
      case 'tid':
        populateClaim(
          key,
          claims[key],
          'The id of the tenant where this app resides. You can use this claim to ensure that only users from the current Azure AD tenant can access this app.',
          index,
          claimsObj,
        );
        index = index + 1;
        break;
      case 'upn':
        populateClaim(
          key,
          claims[key],
          '(user principal name) – might be unique amongst the active set of users in a tenant but tend to get reassigned to new employees as employees leave the organization and others take their place or might change to reflect a personal change like marriage.',
          index,
          claimsObj,
        );
        index = index + 1;
        break;
      case 'email':
        populateClaim(
          key,
          claims[key],
          'Email might be unique amongst the active set of users in a tenant but tend to get reassigned to new employees as employees leave the organization and others take their place.',
          index,
          claimsObj,
        );
        index = index + 1;
        break;
      case 'acct':
        populateClaim(
          key,
          claims[key],
          'Available as an optional claim, it lets you know what the type of user (homed, guest) is. For example, for an individual’s access to their data you might not care for this claim, but you would use this along with tenant id (tid) to control access to say a company-wide dashboard to just employees (homed users) and not contractors (guest users).',
          index,
          claimsObj,
        );
        index = index + 1;
        break;
      case 'sid':
        populateClaim(key, claims[key], 'Session ID, used for per-session user sign-out.', index, claimsObj);
        index = index + 1;
        break;
      case 'sub':
        populateClaim(
          key,
          claims[key],
          'The sub claim is a pairwise identifier - it is unique to a particular application ID. If a single user signs into two different apps using two different client IDs, those apps will receive two different values for the subject claim.',
          index,
          claimsObj,
        );
        index = index + 1;
        break;
      case 'ver':
        populateClaim(
          key,
          claims[key],
          'Version of the token issued by the Microsoft identity platform',
          index,
          claimsObj,
        );
        index = index + 1;
        break;
      case 'login_hint':
        populateClaim(
          key,
          claims[key],
          ' An opaque, reliable login hint claim. This claim is the best value to use for the login_hint OAuth parameter in all flows to get SSO.',
          index,
          claimsObj,
        );
        index = index + 1;
        break;
      case 'idtyp':
        populateClaim(
          key,
          claims[key],
          'Value is app when the token is an app-only token. This is the most accurate way for an API to determine if a token is an app token or an app+user token',
          index,
          claimsObj,
        );
        index = index + 1;
        break;
      case 'roles':
        populateClaim(
          key,
          claims[key],
          'The set of roles that were assigned to the user who is logging in.',
          index,
          claimsObj,
        );
        index = index + 1;
        break;
      case 'uti':
      case 'rh':
        index = index + 1;
        break;
      default:
        populateClaim(key, claims[key], '', index, claimsObj);
        index = index + 1;
    }
  });

  return claimsObj;
};

type TClaimValue = string | number | [];
type TClaimArray = [string, TClaimValue, string];
type TClaimsObject = Record<string, TClaimArray>;

/**
 * Populates claim, description, and value into an claimsObject
 * @param {String} claim
 * @param {String} value
 * @param {String} description
 * @param {Number} index
 * @param {Object} claimsObject
 */
const populateClaim = (
  claim: string,
  value: string | number | [],
  description: string,
  index: number,
  claimsObject: TClaimsObject,
) => {
  claimsObject[index] = [claim, value, description];
};

/**
 * Transforms Unix timestamp to date and returns a string value of that date
 * @param {String} date Unix timestamp
 * @returns
 */
const changeDateFormat = (date: number) => {
  const dateObj = new Date(date * 1000);
  return `${date} - [${dateObj.toString()}]`;
};
